import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface CardProps {
  children: ReactNode;
  className?: string;
  /** 便于列表页做点击进入，用 div + role 保持可访问性。 */
  as?: 'div' | 'section' | 'article' | 'li';
}

export function Card({ children, className, as: Tag = 'div' }: CardProps) {
  return (
    <Tag className={cn('rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5', className)}>
      {children}
    </Tag>
  );
}

export function CardHeader({
  title,
  description,
  action,
  actionAlign = 'end',
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  /**
   * 动作区摆在哪一侧。默认 `'end'` = **保持原样**（沿用目标页/其它页的右上角）。
   *
   * `'start'` 是 2026-10-14 为家长端作业页加的：用户实测要求那一组按钮
   * 「左对齐，靠左摆」。两条类名分开写（而不是 cn 拼），这样**默认那一支的输出
   * 与改动前逐字节相同**，不传这个 prop 的调用点（别的页面）零影响。
   * 传 `'start'` 时，调用点自己的那个动作容器应带 `basis-full`（占满一行、
   * 落到标题下面，而不是把标题挤窄）。
   */
  actionAlign?: 'end' | 'start';
}) {
  return (
    <div
      className={
        actionAlign === 'start'
          ? 'mb-3 flex flex-wrap items-start justify-start gap-x-3 gap-y-2'
          : 'mb-3 flex items-start justify-between gap-3'
      }
    >
      <div className="min-w-0">
        <h2 className="truncate text-base font-semibold text-slate-900">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
      </div>
      {action}
    </div>
  );
}

/** 空状态：统一文案风格，避免每个页面各写一套。 */
export function EmptyState({
  title,
  description,
  action,
  icon = '📭',
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-10 text-center">
      <span aria-hidden="true" className="text-3xl">
        {icon}
      </span>
      <p className="font-medium text-slate-700">{title}</p>
      {description && <p className="max-w-sm text-sm text-slate-500">{description}</p>}
      {action}
    </div>
  );
}

/** 骨架屏：接口未就绪 / 加载中避免页面跳动。 */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-lg bg-slate-200', className)} />;
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'brand' | 'success' | 'warn' | 'danger';
}) {
  const tones: Record<string, string> = {
    neutral: 'bg-slate-100 text-slate-600',
    brand: 'bg-brand-50 text-brand-700',
    success: 'bg-emerald-50 text-emerald-700',
    warn: 'bg-amber-50 text-amber-700',
    danger: 'bg-rose-50 text-rose-700',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}
